import axios from "axios";
import { getToken } from "./userService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Anti-Fraud Service
 * Handles all API calls related to session validation and anti-fraud system
 */

/**
 * Record a webcam check during session
 * @param {number} recordingId - Recording session ID
 * @param {Object} checkData - Webcam check data
 * @param {boolean} checkData.faceDetected - Whether a face was detected
 * @param {number} checkData.arousal - Arousal value from Morphcast
 * @param {number} checkData.valence - Valence value from Morphcast
 * @param {number} [checkData.attention] - Attention value if available
 * @param {number} checkData.confidence - Detection confidence (0-1)
 * @param {string} checkData.timestamp - ISO timestamp of check
 * @returns {Promise<Object>} Check recording result
 */
export async function recordWebcamCheck(recordingId, checkData) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/recordings/${recordingId}/webcam-check`,
      {
        faceDetected: checkData.faceDetected,
        arousal: checkData.arousal,
        valence: checkData.valence,
        attention: checkData.attention,
        confidence: checkData.confidence,
        timestamp: checkData.timestamp,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error recording webcam check:", error);
    throw error;
  }
}

/**
 * Submit evidence URLs incrementally as each GCS upload completes.
 * Call with gameplay and/or webcam — each asset is independent.
 * @param {number} recordingId - Recording session ID
 * @param {Object} evidenceData - Partial evidence data
 * @param {string} [evidenceData.gameplayVideoUrl] - URL to gameplay video
 * @param {string} [evidenceData.webcamVideoUrl] - URL to webcam video
 * @param {boolean} [evidenceData.webcamVerified] - Whether webcam was verified
 * @param {number} [evidenceData.webcamChecksCount] - Number of webcam checks performed
 * @param {string} [evidenceData.ipAddress] - User's IP address
 * @param {string} [evidenceData.userAgent] - User's browser user agent
 * @returns {Promise<Object>} Result (validation included when gameplay is present)
 */
export async function submitEvidence(recordingId, evidenceData) {
  try {
    const payload = {};
    if (evidenceData.gameplayVideoUrl) {
      payload.gameplayVideoUrl = evidenceData.gameplayVideoUrl;
    }
    if (evidenceData.webcamVideoUrl) {
      payload.webcamVideoUrl = evidenceData.webcamVideoUrl;
    }
    if (evidenceData.webcamVerified !== undefined) {
      payload.webcamVerified = evidenceData.webcamVerified;
    }
    if (evidenceData.webcamChecksCount !== undefined) {
      payload.webcamChecksCount = evidenceData.webcamChecksCount;
    }
    if (evidenceData.ipAddress) {
      payload.ipAddress = evidenceData.ipAddress;
    }
    if (evidenceData.userAgent || navigator.userAgent) {
      payload.userAgent = evidenceData.userAgent || navigator.userAgent;
    }

    if (!payload.gameplayVideoUrl && !payload.webcamVideoUrl) {
      throw new Error("submitEvidence requires gameplayVideoUrl and/or webcamVideoUrl");
    }

    const response = await axios.post(
      `${API_BASE_URL}/recordings/${recordingId}/upload-evidence`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error submitting evidence:", error);
    throw error;
  }
}

/**
 * Get user's session validation status
 * @param {number} [limit=10] - Number of sessions to retrieve
 * @returns {Promise<Object>} User's session validation data
 */
export async function getUserValidationStatus(limit = 10) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/recordings/validation-status`,
      {
        params: { limit },
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting validation status:", error);
    throw error;
  }
}

// ============================================
// ADMIN ENDPOINTS
// ============================================

/**
 * Get flagged sessions for admin review
 * @param {Object} options - Query options
 * @param {string} [options.validationStatus] - Filter by status: 'flagged', 'rejected', 'approved', etc.
 * @param {number} [options.minScore] - Minimum validation score
 * @param {number} [options.limit=20] - Number of sessions to retrieve
 * @param {number} [options.offset=0] - Offset for pagination
 * @returns {Promise<Object>} Flagged sessions data
 */
export async function getFlaggedSessions(options = {}) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/sessions/flagged`,
      {
        params: {
          validationStatus: options.validationStatus,
          minScore: options.minScore,
          limit: options.limit || 20,
          offset: options.offset || 0,
        },
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting flagged sessions:", error);
    throw error;
  }
}

/**
 * Get rejected sessions for admin review
 * @param {Object} options - Query options
 * @param {number} [options.limit=20] - Number of sessions to retrieve
 * @param {number} [options.offset=0] - Offset for pagination
 * @returns {Promise<Object>} Rejected sessions data
 */
export async function getRejectedSessions(options = {}) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/sessions/flagged`,
      {
        params: {
          validationStatus: 'rejected',
          limit: options.limit || 20,
          offset: options.offset || 0,
        },
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting rejected sessions:", error);
    throw error;
  }
}

/**
 * Get detailed session information for review
 * @param {number} sessionId - Session ID
 * @returns {Promise<Object>} Session details
 */
export async function getSessionDetails(sessionId) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/sessions/${sessionId}`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting session details:", error);
    throw error;
  }
}

/**
 * Duration-scaled gameplay/webcam stills extracted on the server.
 * Browser seeking cannot move MediaRecorder webm off t=0.
 * @param {number} sessionId
 * @param {string} [kind] - "gameplay" or "webcam"
 * @param {number} [count] - optional requested frame count
 * @returns {Promise<Object>}
 */
export async function getEvidenceSamples(sessionId, kind, count) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/sessions/${sessionId}/samples`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
        params: {
          ...(kind ? { kind } : {}),
          ...(count != null ? { count } : {}),
        },
        timeout: 90000,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting evidence samples:", error);
    throw error;
  }
}

/**
 * Request +N equally spaced review stills for a session video.
 * @param {number} sessionId
 * @param {{ kind: 'gameplay'|'webcam', add?: number }} body
 * @returns {Promise<Object>}
 */
export async function requestMoreEvidenceSamples(sessionId, body) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/sessions/${sessionId}/samples`,
      body,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        timeout: 90000,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error requesting more evidence samples:", error);
    throw error;
  }
}

/**
 * Review a session (approve or reject)
 * @param {number} sessionId - Session ID
 * @param {Object} reviewData - Review decision data
 * @param {string} reviewData.decision - "approved" or "rejected"
 * @param {string} [reviewData.reviewNotes] - Admin notes
 * @param {string} [reviewData.fraudReason] - Reason if rejected
 * @returns {Promise<Object>} Review result
 */
export async function reviewSession(sessionId, reviewData) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/sessions/${sessionId}/review`,
      {
        decision: reviewData.decision,
        reviewNotes: reviewData.reviewNotes,
        fraudReason: reviewData.fraudReason,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error reviewing session:", error);
    throw error;
  }
}

/**
 * Bulk review multiple sessions
 * @param {Object} bulkData - Bulk review data
 * @param {number[]} bulkData.sessionIds - Array of session IDs
 * @param {string} bulkData.decision - "approved" or "rejected"
 * @param {string} bulkData.reason - Reason for bulk action
 * @returns {Promise<Object>} Bulk review result
 */
export async function bulkReviewSessions(bulkData) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/sessions/bulk-review`,
      {
        sessionIds: bulkData.sessionIds,
        decision: bulkData.decision,
        reason: bulkData.reason,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error bulk reviewing sessions:", error);
    throw error;
  }
}

/**
 * Get validation statistics for admin dashboard
 * @returns {Promise<Object>} Validation statistics
 */
export async function getValidationStats() {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/sessions/stats`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error getting validation stats:", error);
    throw error;
  }
}

/**
 * Get client IP address (best effort)
 * @returns {Promise<string|null>} IP address or null
 */
export async function getClientIP() {
  try {
    // Try to get from ipify API
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn("Could not retrieve client IP:", error);
    return null;
  }
}


